'use client'

/**
 * The side navigation's Notifications row (#15618).
 *
 * Its own component so the notification stream lives with the row: the
 * stream opens a server connection on mount, and the row is gated on the
 * org's nav layout. Holding the hook in `AppSidebar` opened that connection
 * for every viewer, row or no row — the header's bell only connects when the
 * bell renders, and this keeps the sidebar to the same rule.
 */

import {
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@sai/ui/components'
import Link from 'next/link'

import { Bell } from '@/components/icons'
import { useNotificationStream } from '@/hooks/use-notification-stream'

import { trackSideNavEvent } from './side-nav-tracking'
import { SIDEBAR_ROW_FOCUS_RING } from './sidebar-focus-ring'

export function SidebarNotificationsItem({
  userId,
  isActive,
  onNavigate,
}: {
  userId: string
  isActive: boolean
  /** Closes the mobile sheet. */
  onNavigate: () => void
}) {
  const { unreadCount } = useNotificationStream({ userId })

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip="Notifications"
        className={SIDEBAR_ROW_FOCUS_RING}
      >
        <Link
          href="/notifications"
          data-testid="nav-notifications"
          aria-label={`Notifications${
            unreadCount > 0 ? ` (${unreadCount} unread)` : ''
          }`}
          onClick={() => {
            trackSideNavEvent({ event: 'item_clicked', key: 'notifications' })
            onNavigate()
          }}
        >
          <Bell />
          <span>Notifications</span>
        </Link>
      </SidebarMenuButton>
      {unreadCount > 0 && (
        <SidebarMenuBadge className="top-2.5 bg-fill-10" aria-hidden="true">
          {unreadCount > 99 ? '99+' : unreadCount}
        </SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  )
}
